import { defineComponent } from 'vue';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods.js';
import TranslationContainer from '@/Application/components/TranslationContainer.jsx';
import { getMessagePlainText } from '../../../Shared/conversationMedia.js';
import { outlinePrimaryQBtnClassNames } from '../../../Shared/buttonAppearance.js';

import {
    QBtn,
    QCard,
    QCardSection,
    QIcon,
    QSpinner,
    QToolbar,
    QToolbarTitle,
} from 'quasar';

const EMAIL_CHANNELS = new Set(['EMail', 'Email', 'email']);

/** Vue3 UI Refresh palette (see Assets/main.css). */
const SM = {
    white: 'var(--v-white-base)',
    primaryText: 'var(--v-primaryText-base)',
    secondaryText: 'var(--v-secondaryText-base)',
    border: 'var(--v-lightGreyBorder-base)',
    customerBg: '#F5F8FB',
    agentBg: 'var(--v-activeBackground-base)',
    radius: 'var(--cc-radius, 8px)',
};

function stripHtml(value) {
    if (!value) return '';
    const text = String(value)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .trim();
    return text;
}

function beginChatData(entry) {
    const msg = entry?.content?.Message || entry?.content?.message;
    return msg?.beginChatData || msg?.BeginChatData || entry?.content?.beginChatData || entry?.content?.BeginChatData || null;
}

function beginChatMessageText(data) {
    if (!data) return '';
    const parts = [];
    if (data.Name) parts.push(`Name: ${data.Name}`);
    if (data.Email) parts.push(`Email: ${data.Email}`);
    return parts.join('\n');
}

function looksLikePhone(value) {
    if (value == null) return false;
    const s = String(value).trim();
    if (!s) return false;
    // Digits / + / spaces / dashes / parens — not a display name
    return /^[+()\d\s.-]{7,}$/.test(s) && /\d{7,}/.test(s.replace(/\D/g, ''));
}

function looksLikeGuid(value) {
    if (value == null) return false;
    const s = String(value).trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/** True for phones, GUIDs, CTX prefixes, USER* tokens — not a supervisor-facing name. */
function looksLikeOpaqueId(value) {
    if (value == null) return true;
    const s = String(value).trim();
    if (!s) return true;
    if (looksLikePhone(s) || looksLikeGuid(s)) return true;
    if (/^_?CTXPFX_/i.test(s)) return true;
    if (/^USER/i.test(s)) return true;
    // Digits / separators only (no letters) — contact/session style ids
    if (!/[a-zA-Z]/.test(s) && /[\d_+-]/.test(s)) return true;
    return false;
}

/** CallerName / contact display only when it looks like a human name. */
function safeCallerDisplayName(value) {
    if (value == null) return '';
    const s = String(value).trim();
    if (!s || looksLikeOpaqueId(s)) return '';
    return s;
}

function messageText(entry) {
    if (!entry) return '';
    const begin = beginChatData(entry);
    if (begin) return beginChatMessageText(begin);
    if (typeof entry.message === 'string' && entry.message) return stripHtml(entry.message);

    const plain = getMessagePlainText(entry);
    if (plain) return stripHtml(plain);

    const content = entry.content;
    if (!content) return '';
    if (typeof content === 'string') return stripHtml(content);
    if (content.Body) return stripHtml(content.Body);
    if (content.Message?.prequeueAnswers && Array.isArray(content.Message.prequeueAnswers)) {
        return content.Message.prequeueAnswers
            .map((a) => {
                const answer = a?.Answer;
                if (typeof answer === 'string') return `${a.Question || ''}: ${answer}`;
                if (Array.isArray(answer)) return `${a.Question || ''}: ${answer.join(', ')}`;
                if (answer?.Text != null) return `${a.Question || ''}: ${answer.Text}`;
                return `${a.Question || ''}: ${answer ?? ''}`;
            })
            .join('\n');
    }
    return '';
}

/** Same-line value after "Name:" — never cross newlines (\s includes \n). */
function nameFromLabeledText(text) {
    if (!text) return '';
    // [ \t]* only — do not use \s after the colon or a blank "Name:" line grabs "Email: …"
    const m = String(text).match(/(?:^|\n)[ \t]*Name[ \t]*:[ \t]*([^\n\r]*)/i);
    if (!m) return '';
    const raw = (m[1] || '').trim();
    // Reject empty or another field label on a mangled line
    if (!raw || /^(email|phone|e-mail)\s*:/i.test(raw)) return '';
    return safeCallerDisplayName(raw);
}

/** Pull a human name from begin-chat / prequeue / "Name: …" on early transcript rows. */
function nameFromMessageEntry(entry) {
    if (!entry) return '';
    const begin = beginChatData(entry);
    if (begin) {
        // Structured begin-chat: only use Name. Do not scrape formatted "Name:\nEmail:" text.
        return safeCallerDisplayName(begin.Name || begin.name || begin.FullName) || '';
    }

    const msg = entry?.content?.Message || entry?.content?.message || {};
    const fromMsg = safeCallerDisplayName(
        msg.name || msg.Name || msg.participantName || msg.ParticipantName
            || entry.participantName || entry.ParticipantName,
    );
    if (fromMsg) return fromMsg;

    const answers = msg.prequeueAnswers || msg.PrequeueAnswers;
    if (Array.isArray(answers)) {
        for (let i = 0; i < answers.length; i += 1) {
            const a = answers[i];
            const q = `${a?.Question || a?.question || ''}`.toLowerCase();
            if (!/\bname\b/.test(q)) continue;
            const answer = a?.Answer ?? a?.answer;
            const raw = typeof answer === 'string'
                ? answer
                : (answer?.Text != null ? answer.Text : (Array.isArray(answer) ? answer.join(' ') : ''));
            const fromAnswer = safeCallerDisplayName(raw);
            if (fromAnswer) return fromAnswer;
        }
    }

    return nameFromLabeledText(messageText(entry));
}

/** Prefer earliest transcript rows (begin-chat is usually first). */
function nameFromTranscriptMessages(messages) {
    const list = messages || [];
    for (let i = 0; i < list.length; i += 1) {
        const found = nameFromMessageEntry(list[i]);
        if (found) return found;
    }
    return '';
}

function isSystemMessage(entry) {
    return entry?.fromid === 'System' || entry?.sender === 'System';
}

function isCustomerMessage(entry) {
    if (!entry || isSystemMessage(entry)) return false;
    if (entry.fromid === 'Caller' || entry.fromid === 'EndUser') return true;
    const direction = (entry.direction || '').toString().toLowerCase();
    return direction === 'inbound' || direction === 'ib';
}

function parseEntryDate(entry) {
    const raw = entry?.time || entry?.timestamp;
    if (!raw) return null;
    try {
        const parsed = typeof raw === 'string' && raw.startsWith('"') ? JSON.parse(raw) : raw;
        // ticks (.NET) are large integers
        if (typeof parsed === 'number' && parsed > 1e12) {
            const ms = Math.floor((parsed - 621355968000000000) / 10000);
            const d = new Date(ms);
            if (!Number.isNaN(d.getTime())) return d;
        }
        const d = new Date(parsed);
        return Number.isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
}

function formatMessageDateTime(entry) {
    const d = parseEntryDate(entry);
    if (!d) return '';
    return d.toLocaleString();
}

function formatMessageClock(entry) {
    const d = parseEntryDate(entry);
    if (!d) return '';
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatRelative(fromMs, nowMs) {
    if (!fromMs) return '';
    const seconds = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Same matching rules as InteractionHistoryViewer.messageMatchesSession */
function messageMatchesSession(message, sessionId) {
    if (!message || !sessionId) return false;
    const candidates = [
        message.interactionid,
        message.sessionid,
        message.SessionId,
        message.content && message.content.SessionId,
        message.content && message.content.Token,
    ];
    const wanted = String(sessionId);
    return candidates.some((id) => id != null && String(id) === wanted);
}

function sortOldestFirst(list) {
    return [...(list || [])].sort((a, b) => {
        const da = parseEntryDate(a)?.getTime() ?? 0;
        const db = parseEntryDate(b)?.getTime() ?? 0;
        return da - db;
    });
}

function normalizeHistoryRows(history) {
    if (!history) return [];
    if (Array.isArray(history)) return history;
    if (Array.isArray(history.Rows)) return history.Rows;
    if (Array.isArray(history.rows)) return history.rows;
    if (Array.isArray(history.Messages)) return history.Messages;
    if (Array.isArray(history.messages)) return history.messages;
    return [];
}

/** Session id used for EmailLogs / timeline (same rules as HistoryItemEmail). */
function emailSessionId(entry, fallbackSessionId) {
    if (!entry) return fallbackSessionId || '';
    return (
        entry.content?.SessionId
        || entry.content?.Token
        || entry.interactionid
        || entry.sessionid
        || entry.SessionId
        || fallbackSessionId
        || ''
    ).toString();
}

function looksLikeHtml(value) {
    return typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);
}

/**
 * Cut index for plain text (newline-separated, e.g. after stripHtml).
 * Returns -1 when no reply header is found (or it would wipe the whole body).
 */
function findReplyHeaderCutIndexPlain(text) {
    if (!text || typeof text !== 'string') return -1;
    const n = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const markers = [
        /(?:^|\n)-{2,}\s*Original Message\s*-{2,}/i,
        /(?:^|\n)-{5,}Original Message-{5,}/i,
        /(?:^|\n)On .+ wrote:\s*\n/i,
        /(?:^|\n)From:\s[^\n]+\nSent:\s/i,
        /(?:^|\n)From:\s[^\n]+\nDate:\s/i,
        /(?:^|\n)_{5,}\s*\nFrom:\s/i,
        /(?:^|\n)From:\s[^\n]+\nTo:\s[^\n]+\nSubject:\s/i,
    ];
    let cut = -1;
    markers.forEach((re) => {
        const m = n.match(re);
        if (m && m.index != null && (cut < 0 || m.index < cut)) cut = m.index;
    });
    return cut > 0 ? cut : -1;
}

/**
 * Cut index against DOM textContent (TreeWalker order — often no newlines between blocks).
 * Matches Outlook "From: … Sent: …" even when tags flatten whitespace.
 */
function findReplyHeaderCutIndexTextContent(text) {
    if (!text || typeof text !== 'string') return -1;
    const markers = [
        /-{2,}\s*Original Message\s*-{2,}/i,
        /From:\s[\s\S]{1,300}?Sent:\s/i,
        /From:\s[\s\S]{1,300}?Date:\s/i,
    ];
    let cut = -1;
    markers.forEach((re) => {
        const m = text.match(re);
        if (m && m.index != null && m.index > 0 && (cut < 0 || m.index < cut)) cut = m.index;
    });
    return cut;
}

function removeNodeAndFollowingSiblings(start) {
    let node = start;
    while (node) {
        const remove = node;
        node = node.nextSibling;
        remove.parentNode?.removeChild(remove);
    }
}

function nextNonEmptyElement(el) {
    let next = el?.nextElementSibling;
    while (next && !(next.textContent || '').trim()) next = next.nextElementSibling;
    return next || null;
}

/** True when this element (plus a few siblings) looks like Outlook reply headers. */
function elementStartsOutlookQuote(el) {
    if (!el) return false;
    const first = (el.textContent || '').trim();
    if (!/^From:\s/i.test(first) && !/^From:/i.test(first.split('\n')[0] || '')) return false;
    let probe = '';
    let n = el;
    for (let i = 0; i < 8 && n; i += 1) {
        probe += `${n.textContent || ''}\n`;
        n = n.nextElementSibling;
    }
    return /From:\s[\s\S]+?\nSent:\s/i.test(probe)
        || /From:\s[\s\S]+?Sent:\s/i.test(probe)
        || /From:\s[\s\S]+?\nDate:\s/i.test(probe);
}

/** Lift to the outermost block that still starts the quote (usually a body child). */
function quoteBlockRoot(el, body) {
    let cur = el;
    while (cur.parentElement && cur.parentElement !== body) {
        const parent = cur.parentElement;
        // Stop elevating once the parent also contains content before this quote block
        let prev = cur.previousSibling;
        let hasPrior = false;
        while (prev) {
            if ((prev.textContent || '').trim()) {
                hasPrior = true;
                break;
            }
            prev = prev.previousSibling;
        }
        if (hasPrior) break;
        cur = parent;
    }
    return cur;
}

/** Truncate a DOM root so only the first `offset` characters of textContent remain. */
function truncateDomAtTextOffset(root, offset) {
    if (!root || offset < 0) return;
    let count = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const removeNodes = [];
    let node = walker.nextNode();
    while (node) {
        const value = node.nodeValue || '';
        const len = value.length;
        if (count + len <= offset) {
            count += len;
            node = walker.nextNode();
            continue;
        }
        if (count < offset) {
            node.nodeValue = value.slice(0, offset - count);
            count = offset;
        } else {
            removeNodes.push(node);
        }
        node = walker.nextNode();
    }
    removeNodes.forEach((n) => n.parentNode?.removeChild(n));

    // Remove the element that contained the cut (and following siblings) when it is now empty/quote-only
    [...root.querySelectorAll('hr, div, p, span, section, font')].reverse().forEach((child) => {
        if (child.tagName === 'HR') {
            if (!(child.nextSibling && (root.textContent || '').includes('\u0000'))) {
                let sib = child.nextSibling;
                let hasTextAfter = false;
                while (sib) {
                    if ((sib.textContent || '').trim()) {
                        hasTextAfter = true;
                        break;
                    }
                    sib = sib.nextSibling;
                }
                if (!hasTextAfter) child.remove();
            }
            return;
        }
        if (!(child.textContent || '').trim() && !child.querySelector('img,table')) child.remove();
    });
}

/** Remove quoted reply / previous-message blocks from email HTML. */
function stripQuotedEmailHtml(html) {
    if (!html || typeof html !== 'string') return html || '';
    if (typeof DOMParser === 'undefined') return html;
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.body;
        if (!body) return html;

        [
            'blockquote',
            '.gmail_quote',
            '.gmail_extra',
            '.gmail_attr',
            '#divRplyFwdMsg',
            '#mail-editor-reference-message-container',
            '.OutlookMessageHeader',
            '[id^="divRplyFwdMsg"]',
            '[name="quote"]',
        ].forEach((sel) => {
            body.querySelectorAll(sel).forEach((el) => el.remove());
        });

        // Outlook: <hr> then From:/Sent:
        body.querySelectorAll('hr').forEach((hr) => {
            const next = nextNonEmptyElement(hr);
            if (next && elementStartsOutlookQuote(next)) {
                removeNodeAndFollowingSiblings(hr);
            }
        });

        // border-top wrappers (common Outlook style)
        [...body.querySelectorAll('div, p, section')].forEach((el) => {
            if (el.parentNode !== body && !el.parentElement) return;
            const style = (el.getAttribute('style') || '').toLowerCase();
            if (!style.includes('border-top')) return;
            if (elementStartsOutlookQuote(el) || /^From:\s/i.test((el.textContent || '').trim())) {
                removeNodeAndFollowingSiblings(quoteBlockRoot(el, body));
            }
        });

        // Element walk: first From:+Sent: block → drop it and everything after in its parent
        if (/From:\s[\s\S]+?Sent:\s/i.test(body.textContent || '')) {
            const candidates = body.querySelectorAll('div, p, span, font, section, td, li');
            for (let i = 0; i < candidates.length; i += 1) {
                const el = candidates[i];
                if (!elementStartsOutlookQuote(el)) continue;
                const block = quoteBlockRoot(el, body);
                const prev = block.previousElementSibling;
                if (prev && prev.tagName === 'HR') {
                    removeNodeAndFollowingSiblings(prev);
                } else {
                    removeNodeAndFollowingSiblings(block);
                }
                break;
            }
        }

        // textContent-offset fallback (same coordinate space as TreeWalker)
        const plain = body.textContent || '';
        const cutAt = findReplyHeaderCutIndexTextContent(plain);
        if (cutAt >= 0 && cutAt < plain.length) {
            truncateDomAtTextOffset(body, cutAt);
        }

        return body.innerHTML;
    } catch {
        return html;
    }
}

/** Cut plain-text bodies at common reply markers. */
function stripQuotedEmailText(text) {
    if (!text || typeof text !== 'string') return text || '';
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const cutAt = findReplyHeaderCutIndexPlain(normalized);
    if (cutAt < 0) {
        // Flat text without newlines between headers
        const flatCut = findReplyHeaderCutIndexTextContent(normalized);
        if (flatCut < 0) return text.trimEnd();
        return normalized.slice(0, flatCut).trimEnd();
    }
    return normalized.slice(0, cutAt).trimEnd();
}

function messageSurfaceBg(entry) {
    return isCustomerMessage(entry) ? SM.customerBg : SM.agentBg;
}

/** Meta WhatsApp only for now — mirrors Agent X MESSAGE_STATUS_CHANNELS. */
function isMetaWhatsAppChannel(channelType) {
    return String(channelType || '').toLowerCase() === 'metawhatsapp';
}

function entryChannelType(entry, fallbackChannelType = '') {
    return (entry?.channeltype || entry?.ChannelType || fallbackChannelType || '').toString();
}

function getEntryMessageStatus(entry) {
    const content = entry?.content?.Message || entry?.content?.message || {};
    const raw = content.Status || content.status || entry?.status || entry?.Status || '';
    return String(raw || '').toLowerCase();
}

function isOutboundAgentMessage(entry) {
    if (!entry || isSystemMessage(entry) || isCustomerMessage(entry)) return false;
    const direction = (entry.direction || '').toString().toLowerCase();
    if (direction === 'inbound' || direction === 'ib') return false;
    return true;
}

/**
 * Agent X parity: always show Failed (incl. retry duplicates); otherwise only the
 * latest Meta WhatsApp outbound in the monitored session.
 */
function shouldShowEntryMessageStatus(entry, messages, fallbackChannelType = '') {
    if (!isOutboundAgentMessage(entry)) return false;
    const channel = entryChannelType(entry, fallbackChannelType);
    if (!isMetaWhatsAppChannel(channel)) return false;

    // Failed retries / duplicates always surface status (never hide them).
    if (getEntryMessageStatus(entry) === 'failed') return true;

    const wanted = String(channel).toLowerCase();
    const outbound = (messages || []).filter((m) => {
        if (!isOutboundAgentMessage(m)) return false;
        return String(entryChannelType(m, fallbackChannelType)).toLowerCase() === wanted;
    });
    if (!outbound.length) return false;
    const latest = outbound[outbound.length - 1];
    const entryId = entry?.msgid != null ? String(entry.msgid) : '';
    const latestId = latest?.msgid != null ? String(latest.msgid) : '';
    if (entryId && latestId) return entryId === latestId;
    return entry === latest;
}

function messageStatusMeta(status) {
    switch (String(status || '').toLowerCase()) {
        case 'sending':
            return {
                icon: 'mdi-clock-outline',
                secondIcon: '',
                color: 'var(--v-secondaryText-base, #757575)',
                label: 'Sending',
            };
        case 'sent':
            return {
                icon: 'mdi-check',
                secondIcon: '',
                color: 'var(--v-secondaryText-base, #757575)',
                label: 'Sent',
            };
        case 'delivered':
            return {
                icon: 'mdi-check',
                secondIcon: 'mdi-check',
                color: 'var(--v-secondaryText-base, #757575)',
                label: 'Delivered',
            };
        case 'read':
            return {
                icon: 'mdi-check',
                secondIcon: 'mdi-check',
                color: 'var(--q-primary)',
                label: 'Read',
            };
        case 'failed':
            return {
                icon: 'mdi-alert-circle-outline',
                secondIcon: '',
                color: 'var(--q-negative)',
                label: 'Failed',
            };
        default:
            return { icon: '', secondIcon: '', color: SM.secondaryText, label: '' };
    }
}

export default defineComponent({
    name: 'DisplaySilentMonitoring',
    emits: ['closeme'],
    components: {
        TranslationContainer,
        QBtn,
        QCard,
        QCardSection,
        QIcon,
        QSpinner,
        QToolbar,
        QToolbarTitle,
    },
    data: () => ({
        vars: {},
        contactId: '',
        sessionId: '',
        channelType: '',
        contact: null,
        messages: [],
        userNamesById: {},
        fullEmailsBySession: {},
        loading: false,
        error: null,
        lastUpdatedMs: null,
        nowTick: Date.now(),
        nowInterval: null,
        historyKey: 0,
    }),
    props: {
        name: String,
        root: null,
        type: null,
        action: Object,
        context: null,
        scopeitems: null,
        controlscope: null,
    },
    computed: {
        isEmailChannel() {
            const ch = (this.channelType || this.vars.ChannelType || '').toString();
            return EMAIL_CHANNELS.has(ch);
        },
        panelTitle() {
            return 'Monitor Interaction';
        },
        displayName() {
            const fromContact = `${this.contact?.first_display || ''} ${this.contact?.last_display || ''}`.trim();
            return safeCallerDisplayName(fromContact)
                || safeCallerDisplayName(this.vars.CallerName)
                || nameFromTranscriptMessages(this.sessionMessages)
                || nameFromTranscriptMessages(this.messages)
                || 'Customer';
        },
        agentDisplayName() {
            return this.vars.AgentName || this.vars.UserName || this.vars.FullName || 'Agent';
        },
        lastUpdatedLabel() {
            if (!this.lastUpdatedMs) return '';
            return formatRelative(this.lastUpdatedMs, this.nowTick);
        },
        sessionMessages() {
            const sessionId = this.sessionId;
            const list = this.messages || [];
            if (!sessionId) return list;
            const matched = list.filter((m) => messageMatchesSession(m, sessionId));
            // Transcript API is already session-scoped; if ids don't annotate, keep all
            return matched.length ? matched : list;
        },
        chatBubbleMessages() {
            return this.sessionMessages || [];
        },
        conversationStartLabel() {
            const first = this.chatBubbleMessages[0] || this.sessionMessages[0];
            const dt = first ? formatMessageDateTime(first) : '';
            return dt || '';
        },
        emailEntries() {
            return this.sessionMessages.filter((m) => {
                const ch = (m.channeltype || m.ChannelType || '').toString();
                return !ch || EMAIL_CHANNELS.has(ch);
            });
        },
    },
    async created() {
        if (this.action?.ActionData?.Vars) {
            try {
                const varsexpn = utils.compileObject(this, this.action.ActionData.Vars);
                this.vars = utils.evaluateObject(varsexpn, this.action.context) || {};
            } catch (e) {
                utils.log('DisplaySilentMonitoring Vars evaluate failed: ' + e);
            }
        }

        this.contactId = this.vars.ContactId || this.vars.Contact_ID || this.vars.contactId || '';
        this.sessionId = (this.vars.SessionId || this.vars.sessionId || '').toString();
        this.channelType = (this.vars.ChannelType || this.vars.channelType || '').toString();
        this.nowInterval = setInterval(() => {
            this.nowTick = Date.now();
        }, 30000);

        await this.bootstrap();
    },
    beforeUnmount() {
        if (this.nowInterval) clearInterval(this.nowInterval);
        void this.writeMonitorAuditEvent('monitor-stop');
    },
    methods: {
        ...methods,
        async bootstrap() {
            this.loading = true;
            this.error = null;
            try {
                if (!this.contactId && this.sessionId) {
                    await this.resolveContactIdFromSession(this.sessionId);
                }
                // Same stream as voice RTMonitor WriteEvent; do not block transcript load.
                void this.writeMonitorAuditEvent('monitor-start');
                const tasks = [this.loadHistory()];
                if (this.contactId) tasks.push(this.loadContact());
                await Promise.all(tasks);
                if (!this.messages.length && !this.contactId && !this.sessionId) {
                    this.error = 'Contact ID is not available for this interaction.';
                }
            } catch (e) {
                utils.error('Silent monitoring bootstrap failed', e);
                if ((this.messages || []).length) {
                    this.error = 'Unable to refresh interaction history.';
                } else {
                    this.error = 'Unable to load interaction history.';
                }
            } finally {
                this.loading = false;
            }
        },
        /** Viewer audit: PUT Apps/AuditHistory/WriteEvent (not document changelog WriteToAuditHistory). */
        async writeMonitorAuditEvent(eventType) {
            if (!this.sessionId || !eventType) return;
            if (eventType === 'monitor-start') {
                if (this._monitorAuditStartWritten) return;
                this._monitorAuditStartWritten = true;
            } else if (eventType === 'monitor-stop') {
                if (this._monitorAuditStopWritten) return;
                this._monitorAuditStopWritten = true;
            }
            try {
                await utils.api.request(
                    'PUT',
                    `Apps/AuditHistory/WriteEvent?EventType=${encodeURIComponent(eventType)}&Id=${encodeURIComponent(this.sessionId)}`,
                );
            } catch (e) {
                utils.error('Silent monitoring WriteEvent failed', e);
            }
        },
        async resolveContactIdFromSession(sessionId) {
            try {
                const contacts = await utils.api.request('GET', `Apps/Contacts/ContactsBySessionID/${encodeURIComponent(sessionId)}`);
                const first = Array.isArray(contacts) ? contacts[0] : contacts;
                this.contactId = first?.contact_id || first?.Contact_ID || first?.ContactId || '';
            } catch (e) {
                utils.error('ContactsBySessionID failed', e);
            }
        },
        async loadContact() {
            try {
                this.contact = await utils.api.request('GET', `Apps/Contacts/Contact/${encodeURIComponent(this.contactId)}`);
            } catch (e) {
                utils.error('Contact load failed', e);
                this.contact = null;
            }
        },
        async loadSessionTranscript() {
            if (!this.sessionId) return [];
            try {
                const transcript = await utils.api.request(
                    'GET',
                    `Apps/Common/Chat/GetTranscript/${encodeURIComponent(this.sessionId)}?MaxRows=1000`,
                );
                return normalizeHistoryRows(transcript);
            } catch (e) {
                utils.error('GetChatTranscript failed', e);
                return [];
            }
        },
        async loadContactHistory() {
            if (!this.contactId) return [];
            try {
                const history = await utils.api.request(
                    'GET',
                    `Apps/OmniChannel/GetInteractionHistory?Contact_ID=${encodeURIComponent(this.contactId)}&maxRows=100`,
                );
                return normalizeHistoryRows(history);
            } catch (e) {
                utils.error('GetInteractionHistory failed', e);
                return [];
            }
        },
        async loadHistory() {
            let rows = [];

            if (this.isEmailChannel) {
                rows = await this.loadContactHistory();
                if (this.sessionId) {
                    const matched = rows.filter((m) => messageMatchesSession(m, this.sessionId));
                    rows = matched.length ? matched : rows;
                }
            } else {
                // Live written channels: session transcript is the source of truth
                rows = await this.loadSessionTranscript();
                if (!rows.length && this.contactId) {
                    const contactRows = await this.loadContactHistory();
                    rows = this.sessionId
                        ? contactRows.filter((m) => messageMatchesSession(m, this.sessionId))
                        : contactRows;
                    if (!rows.length) rows = contactRows;
                }
            }

            this.messages = sortOldestFirst(rows);
            if (this.isEmailChannel) {
                await this.loadFullEmails(this.messages);
            }
            await this.resolveAgentNames(this.messages);
            this.lastUpdatedMs = Date.now();
            this.nowTick = Date.now();
            this.historyKey += 1;
        },
        async loadFullEmail(sessionId) {
            if (!sessionId) return null;
            if (this.fullEmailsBySession[sessionId]) return this.fullEmailsBySession[sessionId];

            // Same path as Recents / HistoryItemEmail: timeline EmailLog, then Content.json fallback
            try {
                const timeline = await utils.api.request(
                    'GET',
                    `Apps/ACD/Reports/GeneralTimeline?TimelineId=${encodeURIComponent(sessionId)}`,
                );
                const attributes = Array.isArray(timeline) && timeline.length ? timeline[0]?.attributes : null;
                if (attributes?.EmailLog) {
                    const email = await utils.api.request(
                        'GET',
                        `Apps/MassStorage/GetFileJson?fullNameWithPath=${encodeURIComponent(attributes.EmailLog)}`,
                    );
                    if (email && typeof email === 'object') return email;
                }
            } catch (e) {
                utils.error('Email timeline/EmailLog load failed', e);
            }

            try {
                const email = await utils.api.request(
                    'GET',
                    `Apps/MassStorage/GetFileJson?fullNameWithPath=${encodeURIComponent(`EmailLogs/${sessionId}/Content.json`)}`,
                );
                return email && typeof email === 'object' ? email : null;
            } catch (e) {
                utils.error('EmailLogs Content.json load failed', e);
                return null;
            }
        },
        async loadFullEmails(rows) {
            const ids = new Set();
            (rows || []).forEach((entry) => {
                const id = emailSessionId(entry, this.sessionId);
                if (id && !this.fullEmailsBySession[id]) ids.add(id);
            });
            // Also load the panel session even if history rows are empty/mismatched
            if (this.sessionId && !this.fullEmailsBySession[this.sessionId]) {
                ids.add(this.sessionId);
            }
            if (!ids.size) return;

            const updates = {};
            await Promise.all([...ids].map(async (id) => {
                const email = await this.loadFullEmail(id);
                if (email) updates[id] = email;
            }));
            if (Object.keys(updates).length) {
                this.fullEmailsBySession = { ...this.fullEmailsBySession, ...updates };
            }
        },
        normalizeUserId(raw) {
            if (raw == null) return '';
            let id = String(raw).trim();
            if (!id || id === 'System' || id === 'Caller' || id === 'EndUser') return '';
            if (id.startsWith('USER')) id = id.substring(4);
            if (looksLikePhone(id)) return '';
            return id;
        },
        async resolveAgentNames(rows) {
            const ids = new Set();
            const seedName = this.vars.AgentName || this.vars.UserName || this.vars.FullName || '';
            const seedId = this.normalizeUserId(this.vars.UserID || this.vars.AgentUserId || this.vars.UserId);
            if (seedId && seedName && seedName !== 'Agent') {
                this.userNamesById = { ...this.userNamesById, [seedId]: seedName };
            }

            (rows || []).forEach((entry) => {
                if (isSystemMessage(entry) || isCustomerMessage(entry)) return;
                const id = this.normalizeUserId(entry?.fromid);
                if (id && !this.userNamesById[id]) ids.add(id);
            });

            if (!ids.size) return;

            const updates = {};
            await Promise.all([...ids].map(async (userId) => {
                try {
                    const res = await utils.api.request(
                        'GET',
                        `Apps/User/FullName/${encodeURIComponent(userId)}`,
                    );
                    const name = typeof res === 'string'
                        ? res
                        : (res && (res.FullName || res.Name || res.UserName)) || '';
                    if (name && String(name).trim()) updates[userId] = String(name).trim();
                } catch (e) {
                    utils.error(`User FullName lookup failed for ${userId}`, e);
                }
            }));

            if (Object.keys(updates).length) {
                this.userNamesById = { ...this.userNamesById, ...updates };
            }
        },
        async refresh() {
            if (this.loading) return;
            this.loading = true;
            const hadMessages = (this.messages || []).length > 0;
            this.error = null;
            try {
                await this.loadHistory();
            } catch (e) {
                utils.error('Silent monitoring refresh failed', e);
                this.error = 'Unable to refresh interaction history.';
                if (!hadMessages) this.messages = this.messages || [];
            } finally {
                this.loading = false;
            }
        },
        close() {
            this.$emit('closeme', {
                Success: true,
                DialogResult: {},
                AllowClose: true,
            });
        },
        senderLabel(entry) {
            if (isSystemMessage(entry)) return 'System';
            if (isCustomerMessage(entry)) {
                const name = this.displayName;
                // Avoid "Customer (Customer)" when we have no real display name
                if (!name || name === 'Customer') return 'Customer';
                return `${name} (Customer)`;
            }
            // channel_history.sender is often the customer phone — do not use it for agents
            const fromId = this.normalizeUserId(entry?.fromid);
            const lookedUp = fromId ? this.userNamesById[fromId] : '';
            const chatSender = entry?.content?.Message?.Sender || entry?.content?.Sender;
            let agent = lookedUp || '';
            if (!agent && chatSender && !looksLikeOpaqueId(chatSender) && chatSender !== 'System') {
                agent = String(chatSender);
            }
            if (!agent) agent = safeCallerDisplayName(this.agentDisplayName) || 'Agent';
            // Avoid "Agent (Agent)" / raw ids when we still have no real name
            if (!agent || agent === 'Agent' || looksLikeOpaqueId(agent)) return 'Agent';
            return `${agent} (Agent)`;
        },
        emailMeta(entry) {
            const content = entry?.content || {};
            const sid = emailSessionId(entry, this.sessionId);
            const full = (sid && this.fullEmailsBySession[sid]) || null;
            const html = full?.Html || full?.html || '';
            const plain = full?.Text || full?.text
                || content.Body || content.body || content.Text
                || messageText(entry) || '';
            const isHtml = !!(html && String(html).trim()) || looksLikeHtml(plain);
            const rawHtml = isHtml ? (html || plain) : '';
            const rawText = isHtml ? '' : stripHtml(plain);
            return {
                to: content.To || content.to || full?.To || full?.Recipient || '',
                from: content.From || content.from || full?.From || full?.Sender || '',
                subject: content.Subject || content.subject || full?.Subject || '',
                bodyHtml: rawHtml ? stripQuotedEmailHtml(rawHtml) : '',
                bodyText: rawText ? stripQuotedEmailText(rawText) : '',
                when: formatMessageDateTime(entry),
            };
        },
        renderEmailCards() {
            let entries = this.emailEntries;
            if (!entries.length && this.sessionId && this.fullEmailsBySession[this.sessionId]) {
                entries = [{ content: { SessionId: this.sessionId }, interactionid: this.sessionId }];
            }

            if (!entries.length) {
                return (
                    <div class="text-caption q-pa-md" style={{ color: SM.secondaryText }}>
                        <TranslationContainer context={this} value="No email messages yet." />
                    </div>
                );
            }

            const cards = entries.map((entry, index) => {
                const meta = this.emailMeta(entry);
                return (
                    <div
                        key={`email-${entry.timestamp || entry.time || index}-${this.historyKey}`}
                        class="q-pa-md q-mb-md"
                        style={{
                            backgroundColor: messageSurfaceBg(entry),
                            borderRadius: SM.radius,
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            flex: '0 0 auto',
                            boxSizing: 'border-box',
                        }}
                    >
                        {meta.when ? (
                            <div class="text-caption text-weight-medium q-mb-sm" style={{ color: SM.secondaryText }}>
                                {meta.when}
                            </div>
                        ) : null}
                        {meta.to ? (
                            <div class="text-body2" style={{ color: SM.secondaryText }}>
                                <TranslationContainer context={this} value="To" />: {meta.to}
                            </div>
                        ) : null}
                        {meta.from ? (
                            <div class="text-body2" style={{ color: SM.secondaryText }}>
                                <TranslationContainer context={this} value="From" />: {meta.from}
                            </div>
                        ) : null}
                        {meta.subject ? (
                            <div class="text-body2 text-weight-medium q-mt-xs" style={{ color: SM.primaryText }}>
                                <TranslationContainer context={this} value="Subject" />: {meta.subject}
                            </div>
                        ) : null}
                        <div style={{ borderTop: `1px solid ${SM.border}`, margin: '12px 0' }} />
                        {meta.bodyHtml ? (
                            <div
                                class="text-body2 SilentMonitoringEmailBody"
                                style={{ color: SM.primaryText, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                v-html={meta.bodyHtml}
                            />
                        ) : (
                            <div
                                class="text-body2"
                                style={{ whiteSpace: 'pre-wrap', color: SM.primaryText, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                            >
                                {meta.bodyText || '--'}
                            </div>
                        )}
                    </div>
                );
            });

            return (
                <div
                    class="column no-wrap"
                    style={{ width: '100%', maxWidth: '100%', minWidth: 0, flex: '0 0 auto', flexWrap: 'nowrap' }}
                >
                    {cards}
                </div>
            );
        },
        renderChatMessages() {
            if (!this.chatBubbleMessages.length) {
                return (
                    <div class="text-caption q-pa-md" style={{ color: SM.secondaryText }}>
                        <TranslationContainer context={this} value="No messages yet." />
                    </div>
                );
            }

            const bubbles = this.chatBubbleMessages.map((entry, index) => {
                const customer = isCustomerMessage(entry);
                const system = isSystemMessage(entry);
                const text = messageText(entry);
                if (!text && !system) return null;

                const showStatus = shouldShowEntryMessageStatus(
                    entry,
                    this.chatBubbleMessages,
                    this.channelType,
                );
                // Agent X parity: unresolved latest outbound defaults to "sending".
                const status = getEntryMessageStatus(entry) || (showStatus ? 'sending' : '');
                const statusMeta = showStatus ? messageStatusMeta(status) : null;
                const muteBubble = showStatus && (status === 'sending' || status === 'failed');

                return (
                    <div
                        key={`${entry.msgid || entry.timestamp || entry.time || index}-${this.historyKey}`}
                        class="column no-wrap q-mb-md"
                        style={{
                            alignItems: customer ? 'flex-start' : 'flex-end',
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            flex: '0 0 auto',
                        }}
                    >
                        <div class="text-caption text-weight-medium q-mb-xs" style={{ color: SM.secondaryText }}>
                            {this.senderLabel(entry)}
                            {formatMessageClock(entry) ? ` • ${formatMessageClock(entry)}` : ''}
                        </div>
                        <div
                            class="q-pa-sm"
                            style={{
                                maxWidth: '90%',
                                minWidth: 0,
                                borderRadius: SM.radius,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                backgroundColor: messageSurfaceBg(entry),
                                color: muteBubble ? SM.secondaryText : SM.primaryText,
                                opacity: muteBubble ? 0.75 : 1,
                            }}
                        >
                            {text || '--'}
                        </div>
                        {statusMeta && statusMeta.label ? (
                            <div
                                class="row items-center no-wrap q-mt-xs text-caption"
                                style={{
                                    color: statusMeta.color,
                                    gap: '2px',
                                    maxWidth: '90%',
                                }}
                            >
                                {statusMeta.icon ? (
                                    <QIcon name={statusMeta.icon} size="14px" style={{ color: statusMeta.color }} />
                                ) : null}
                                {statusMeta.secondIcon ? (
                                    <QIcon
                                        name={statusMeta.secondIcon}
                                        size="14px"
                                        style={{ color: statusMeta.color, marginLeft: '-6px' }}
                                    />
                                ) : null}
                                <span>
                                    <TranslationContainer context={this} value={statusMeta.label} />
                                </span>
                            </div>
                        ) : null}
                    </div>
                );
            });

            return (
                <div
                    class="column no-wrap"
                    style={{
                        minHeight: 0,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        flex: '0 0 auto',
                        flexWrap: 'nowrap',
                    }}
                >
                    {this.conversationStartLabel ? (
                        <div
                            class="column no-wrap items-center text-center text-caption text-weight-medium q-mb-md"
                            style={{ color: SM.secondaryText, width: '100%', flex: '0 0 auto' }}
                        >
                            <div>{this.conversationStartLabel}</div>
                            <div>
                                <TranslationContainer context={this} value="~This is the beginning of the conversation~" />
                            </div>
                        </div>
                    ) : null}
                    {bubbles}
                </div>
            );
        },
    },
    render() {
        const showSpinner = this.loading && !(this.messages || []).length;
        const showContentError = !showSpinner && this.error && !(this.messages || []).length;
        const showInlineError = !showSpinner && this.error && (this.messages || []).length > 0;
        const showBody = !showSpinner && !showContentError;

        return (
            <QCard
                class="SilentMonitoringPanel column no-wrap bg-white"
                style={{ height: '100%', width: '100%', maxWidth: '100%', minWidth: 0, backgroundColor: SM.white }}
            >
                <QToolbar class="bg-white text-dark" style={{ minHeight: '56px', backgroundColor: SM.white, color: SM.primaryText }}>
                    <QToolbarTitle style={{ color: SM.primaryText, fontSize: '20px', fontWeight: 500 }}>
                        <TranslationContainer context={this} value={this.panelTitle} />
                    </QToolbarTitle>
                    <QBtn flat dense round onClick={this.close} aria-label="Close">
                        <QIcon name="mdi-close" />
                    </QBtn>
                </QToolbar>

                <QCardSection
                    class="column q-py-sm q-mx-md bg-white"
                    style={{ borderBottom: `1px solid ${SM.border}`, backgroundColor: SM.white, paddingLeft: 0, paddingRight: 0 }}
                >
                    {/* items-center on the row centers Refresh on the gap between the two left lines. */}
                    <div class="row items-center no-wrap full-width justify-between">
                        <div class="col column" style={{ minWidth: 0 }}>
                            <span class="text-caption text-weight-medium" style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 500 }}>
                                <TranslationContainer context={this} value="Last updated" />
                                {this.lastUpdatedLabel ? `: ${this.lastUpdatedLabel}` : ''}
                                {'. '}
                                <TranslationContainer context={this} value="No auto-refresh." />
                            </span>
                            <div class="text-positive text-caption text-weight-medium" style={{ fontWeight: 500 }}>
                                <TranslationContainer context={this} value="Monitoring in progress" />
                            </div>
                        </div>

                        <QBtn
                            outline
                            color="primary"
                            no-caps
                            class={`c-SentenceCaseButton q-ml-sm self-center ${outlinePrimaryQBtnClassNames()}`}
                            onClick={this.refresh}
                            disable={this.loading}
                        >
                            <TranslationContainer context={this} value="Refresh" />
                        </QBtn>
                    </div>
                </QCardSection>

                <QCardSection class="col column no-wrap bg-white" style={{ flex: 1, minHeight: 0, minWidth: 0, maxWidth: '100%', overflow: 'hidden', flexWrap: 'nowrap', backgroundColor: SM.white }}>
                    {showSpinner && (
                        <div class="row flex-center q-pa-lg">
                            <QSpinner color="primary" size="40px" />
                        </div>
                    )}

                    {showContentError && (
                        <div class="text-negative q-pa-md">{this.error}</div>
                    )}

                    {showInlineError && (
                        <div class="text-negative q-px-md q-pb-sm">{this.error}</div>
                    )}

                    {showBody && (
                        <div
                            class="col column no-wrap SilentMonitoringTranscript"
                            style={{
                                flex: '1 1 auto',
                                minHeight: 0,
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                // Quasar .column defaults to flex-wrap:wrap — with a fixed height that
                                // newspaper-columns bubbles sideways instead of scrolling vertically.
                                flexWrap: 'nowrap',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                border: `1px solid ${SM.border}`,
                                borderRadius: SM.radius,
                                padding: '12px',
                                backgroundColor: SM.white,
                                boxSizing: 'border-box',
                            }}
                        >
                            {this.loading ? (
                                <div class="row flex-center q-pa-sm">
                                    <QSpinner color="primary" size="24px" />
                                </div>
                            ) : null}
                            {this.isEmailChannel ? this.renderEmailCards() : this.renderChatMessages()}
                        </div>
                    )}
                </QCardSection>
            </QCard>
        );
    },
});
